import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { auth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from '@/lib/firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const res = await api.get('/auth/me');
          setUser(res.data);
          localStorage.setItem('virtu_user', JSON.stringify(res.data));
        } catch {
          setUser({
            id: firebaseUser.uid,
            name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Usuario',
            email: firebaseUser.email,
          });
        }
      } else {
        localStorage.removeItem('virtu_user');
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const login = useCallback(async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const token = await userCredential.user.getIdToken();
    // Token is handled by api.js interceptor, but store for reference
    localStorage.setItem('virtu_token', token);
    const res = await api.get('/auth/me');
    setUser(res.data);
    localStorage.setItem('virtu_user', JSON.stringify(res.data));
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    localStorage.removeItem('virtu_token');
    localStorage.removeItem('virtu_user');
    setUser(null);
  }, []);

  const updateUser = useCallback((updated) => {
    setUser(updated);
    localStorage.setItem('virtu_user', JSON.stringify(updated));
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
